import React, { useEffect, useMemo, useState } from "react"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { Autocomplete, Box, Button, FormControl, Grid, InputLabel, LinearProgress, MenuItem, Select, SelectChangeEvent, SelectProps, Tab, Tabs, TextField } from "@mui/material"
import { GridSortModel } from "@mui/x-data-grid"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { StateReportBillingList } from "../components/Lists/StateReportBillingList"
import { StateReportsBillingModel } from "../models/StateReportsBillingModel"
import { PartnerHierarchyItem } from "../models/PartnersModel"
import { apiClient } from "../helper/api"
import {
  billingCompanyRollupForApi,
  billingPartnerIdForApi,
  billingSelectDisplayLabel
} from "../helper/partnerBillingSelect"
import useAuth from "../context/AuthProvider"
import { Search, Autorenew, Refresh } from "@mui/icons-material";
import Clear from "@mui/icons-material/Clear";

export enum StateReportTabs {
  Upcoming = 1,
  Completed = 2,
  Outstanding = 3
}

export const StateReportsBilling = () => {
  const { user } = useAuth()
  const token = user?.token
  const [selectedTab, setSelectedTab] = useState<StateReportTabs>(StateReportTabs.Upcoming);
  const [filterModel, setFilterModel] = useState<{ items: { columnField: string, value: string, operatorValue: string }[] }>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'dueDate', sort: 'asc' }
  ]);
  const [itemList, setItemList] = useState<StateReportsBillingModel[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [loading, setloading] = useState(false);
  const [partnersHierarchy, setPartnersHierarchy] = useState<PartnerHierarchyItem[]>([]);
  const [billingSelectValue, setBillingSelectValue] = useState<number | string>(0);
  const billingPartnerIdApi = useMemo(
    () => billingPartnerIdForApi(billingSelectValue, partnersHierarchy),
    [billingSelectValue, partnersHierarchy]
  );
  const selPartnerId = billingPartnerIdApi;
  const [totalCount, setTotalCount] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState<string>((new Date().getMonth() + 1).toString());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  

  const fetchData = async () => {
    const data = await fetchStateReportBillingData(pageSize, currentPage);
    setItemList(data);
  };

  const fetchStateReportBillingData = async (_pageSize: number, _currentPage: number) => {
    setloading(true);
    
    // Calculate dates based on business logic for each tab
    let month = selectedMonth;
    let year = selectedYear;
    
    // Business Logic Implementation:
    // All tabs: Use selected month/year directly
    // If user selects September, show data for September
    month = selectedMonth;
    year = selectedYear;
    
    // Debug logging
    console.log('🔍 STATE REPORTS BILLING API CALL:', {
      selectedTab: selectedTab,
      tabName: selectedTab === 1 ? 'Upcoming' : selectedTab === 2 ? 'Completed' : selectedTab === 3 ? 'Outstanding' : 'Unknown',
      originalMonth: selectedMonth,
      originalYear: selectedYear,
      calculatedMonth: month,
      calculatedYear: year,
      selectedPartner: selPartnerId
    });

    // Create request body object (simplified - no date range)
    const requestBody = {
      pageSize: _pageSize || 50,
      currentPage: _currentPage || 1,
      PartnerId: selPartnerId || '',
      month: month,
      year: year,
      field: sortModel?.[0]?.['field'] || '',
      sort: sortModel?.[0]?.['sort'] || '',
      fieldName: filterModel?.items?.[0]?.['columnField'] || '',
      value: filterModel?.items?.[0]?.['value'] || '',
      operator: filterModel?.items?.[0]?.['operatorValue'] || '',
      selectedTab: selectedTab,
      userTimezone: 'America/Denver',
      companyRollup: billingCompanyRollupForApi(billingSelectValue),
    };



    let data: StateReportsBillingModel[] = []
    await apiClient(`/StateReport/getStateReportBilling`, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(requestBody),
    })
    .then(responseJson => {
      if (responseJson){
        setTotalCount(responseJson.total);
        data = responseJson.data;
      } else {
        data = [];
      }
    })
    .finally(() => {
      setloading(false);
    });
    return data;
  };


  const fetchCustomer = () => {
    apiClient("/Partners/getPartnersHierarchy", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    }).then((responseJson) => {
      if (!responseJson) return
      setPartnersHierarchy(responseJson.data || [])
    })
  }

  useEffect(() => {
    fetchCustomer()
  }, [])

  useEffect(() => {
    setloading(true);
    setBillingSelectValue(0);
    setBillingSelectValue(0);
    setSelectedMonth((new Date().getMonth() + 1).toString());
    setSelectedYear(new Date().getFullYear().toString());
    setFilterModel({ items: [] });
    setSortModel([]);
    setCurrentPage(1);
    setPageSize(25);
    setTotalCount(0);
    setItemList([]);
    setloading(false);
}, [selectedTab]);

  useEffect(() => {
    // Always fetch data when dependencies change
    fetchData();
  }, [
    selPartnerId,
    billingSelectValue,
    selectedMonth,
    selectedYear,
    pageSize,
    currentPage,
    filterModel,
    sortModel,
  ]);
  
  const FMSelectCustomCustomer: React.FC<SelectProps> = ({ variant, ...props }) => {
    return (
      <div>
        <FormControl fullWidth>
        <Autocomplete
            sx={{
              // Parent/Container Style
              marginTop: '-4px !important',
              
              // Target nested Input
              '& .MuiAutocomplete-input': {
                padding: '0px 4px 0px 5px !important',
              },
            }}
            options={partnersHierarchy}
            getOptionLabel={(option) => option.customerName}
            onChange={(event, value) => setBillingSelectValue(value?.partnersId.toString() ?? "0")}
            value={partnersHierarchy.find(item => item.partnersId.toString() === billingSelectValue)}
            renderInput={(params) => {
              return <TextField
                sx={{
                  '.MuiFormLabel-root.Mui-focused': {
                    color: (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0) ? '#000' : '#cccccc !important'
                  },
                  '& .MuiInputBase-root': { marginTop: '5px !important' },
                  '& .MuiOutlinedInput-root': { padding: '4.5px 9px !important' },
                  '& .MuiFormLabel-root': { transform: 'translate(14px, 13px) scale(1)' },
                  '& .MuiInputLabel-shrink': { transform: 'translate(14px, -2px) scale(0.75)' },
                }}
                {...params}
                label="Client"
                InputLabelProps={{ ...params.InputLabelProps, shrink: (parseInt(billingSelectValue?.toString() ?? '0') > 0 || (typeof params.inputProps.value === 'string' && params.inputProps.value.length > 0)) ? true : false }}
              />
            }}
          />
        </FormControl>
      </div>
    );
  };


  function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  function RunSearch() {
    fetchData();
  }

  function ClearSearch() {
    setBillingSelectValue('0');
    setSelectedMonth((new Date().getMonth() + 1).toString());
    setSelectedYear(new Date().getFullYear().toString());
    setCurrentPage(1);
    fetchData();
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
        {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
      </div>
      {/* Top bar: Tabs | Filters toggle | Refresh - match Tabs.tsx */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#fafbfc",
          minHeight: 56,
          // mb: 3,
          marginLeft: { xs: '-24px', lg: '0px' },
          marginRight: { xs: '-24px', lg: '0px' },
        }}
      >
        {/* Tabs: desktop only (md and up) */}
        <Tabs
          value={selectedTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          onChange={(_, newValue: number) => setSelectedTab(newValue as StateReportTabs)}
          sx={{
            // display: { xs: "none", md: "flex" },
            minHeight: 48,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.9375rem" },
            "& .Mui-selected": { color: "#2099d2" },
            "& .MuiTabs-indicator": { backgroundColor: "#2099d2", height: 3, borderRadius: "3px 3px 0 0" },
          }}
        >
          <Tab value={StateReportTabs.Upcoming} label="Upcoming Report" {...a11yProps(0)} />
          <Tab value={StateReportTabs.Completed} label="Completed Report" {...a11yProps(1)} />
          <Tab value={StateReportTabs.Outstanding} label="Outstanding Report" {...a11yProps(2)} />
        </Tabs>
        {/* Select option for mobile/tablet view */}
        {/* <FormControl
          size="small"
          sx={{
            display: { xs: "block", md: "none" },
            minWidth: 160,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
            "& .MuiSelect-select": { fontWeight: 600, color: "#2099d2", py: 0.9 },
          }}
        >
          <Select
            value={selectedTab}
            onChange={(e) => setSelectedTab(Number(e.target.value) as StateReportTabs)}
            displayEmpty
            sx={{ textTransform: "none", fontSize: "0.9375rem" }}
          >
            <MenuItem value={StateReportTabs.Upcoming}>Upcoming Report</MenuItem>
            <MenuItem value={StateReportTabs.Completed}>Completed Report</MenuItem>
            <MenuItem value={StateReportTabs.Outstanding}>Outstanding Report</MenuItem>
          </Select>
        </FormControl> */}
        
      </Box>

      <Box
        sx={{
          width: "100%",
          py: 2,
          px: 2,
          backgroundColor: "#ffffff",
          // borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Grid container spacing={2} className="filter-grid" display="flex">
            {(user?.userRoles.split(",").includes("Super Admin") || user?.userRoles.split(",").includes("MISC State Reporter")) ? (
              <Grid item xl={2} lg={4} sm={6} xs={12}>
                <FMSelectCustomCustomer name="customer" />
              </Grid>
            ) : null}
            <Grid item xl={2} lg={4} sm={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel id="month-Label">Select Month</InputLabel>
                <Select
                  labelId="month-Label"
                  IconComponent={ExpandMoreIcon}
                  label="Select Month"
                  value={selectedMonth}
                  onChange={(event: SelectChangeEvent<string>) => setSelectedMonth(event.target.value)}
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                    // "& .MuiInputBase-root": { marginTop: "-3px !important" },
                    "& .MuiInputBase-input": { padding: "7px 14px !important" },
                  }}
                >
                  <MenuItem value="1">January</MenuItem>
                  <MenuItem value="2">February</MenuItem>
                  <MenuItem value="3">March</MenuItem>
                  <MenuItem value="4">April</MenuItem>
                  <MenuItem value="5">May</MenuItem>
                  <MenuItem value="6">June</MenuItem>
                  <MenuItem value="7">July</MenuItem>
                  <MenuItem value="8">August</MenuItem>
                  <MenuItem value="9">September</MenuItem>
                  <MenuItem value="10">October</MenuItem>
                  <MenuItem value="11">November</MenuItem>
                  <MenuItem value="12">December</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xl={2} lg={4} sm={6} xs={12}>
              <TextField
                label="Year"
                type="number"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                size="small"
                fullWidth
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4c4c4" },
                  // "& .MuiInputBase-root": { marginTop: "-3px !important" },
                  "& .MuiInputBase-input": { padding: "4.5px 14px !important" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={RunSearch}
                  startIcon={<Search />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: "#2099d2",
                    "&:hover": { backgroundColor: "#176990" },
                  }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={ClearSearch}
                  startIcon={<Clear />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: "#e2e8f0",
                    color: "#475569",
                    "&:hover": { borderColor: "#2099d2", color: "#2099d2" },
                  }}
                >
                  Clear
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() => window.location.reload()}
                  startIcon={<Refresh />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: "#e2e8f0",
                    color: "#475569",
                    "&:hover": {
                      borderColor: "#2099d2",
                      backgroundColor: "#2099d214",
                      color: "#2099d2",
                    },
                  }}
                >
                  Refresh
                </Button>
              </Box>
            </Grid>
        </Grid>
      </Box>

      <Box sx={{ width: "100%" }}>
        <Box position="relative">
          {loading && (
            <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                    backgroundColor: "rgba(250, 251, 252, 0.85)",
                    zIndex: 10,
                    border: "1px solid #e2e8f0",
                    borderRadius: 1,
                }}
            >
                <Box sx={{ width: "100%", maxWidth: 320 }}>
                    <LinearProgress color="info" sx={{ backgroundColor: "#e2e8f0", "& .MuiLinearProgress-bar": { backgroundColor: "#132D3D" } }} />
                    <Box sx={{ mt: 1.5, textAlign: "center" }}>
                        <span style={{ fontSize: "14px", fontWeight: 600, color: "#475569" }}>Loading...</span>
                    </Box>
                </Box>
            </Box>
          )}
          <div className={loading ? "invisible" : "visible"}>
            <Box sx={{ p: 3 }}>
              <StateReportBillingList
                listData={itemList}
                loadingData={loading}
                setItemList={setItemList}
                setFilterModel={setFilterModel}
                setSortModel={setSortModel}
                sortModel={sortModel}
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                selectedTab={selectedTab}
                pageSize={pageSize}
                setPageSize={setPageSize}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalCount={totalCount}
                selectedPartner={selPartnerId}
                selectedPartnerDisplayName={billingSelectDisplayLabel(
                  billingSelectValue,
                  partnersHierarchy
                )}
                billingCompanyRollup={billingCompanyRollupForApi(billingSelectValue)}
                fetchStateReportBillingData={fetchStateReportBillingData}
              />
            </Box>
          </div>
        </Box>
      </Box>
    </LocalizationProvider>
  )
}
